import styled from 'styled-components'
import type {UrgencyLevel} from '~/src/models/notification'

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-4);
`

export const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`

export const PageSubtitle = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: var(--neutral-600);
  margin: 0;
`

export const HeaderActions = styled.div`
  display: flex;
  gap: var(--space-2);
  flex-wrap: wrap;
`

export const HeaderActionButton = styled.button<{$variant?: 'primary' | 'secondary'}>`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-sm);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$variant === 'primary' ? 'var(--brand-cta)' : '#e5e7eb')};
  background: ${(p) => (p.$variant === 'primary' ? 'var(--brand-cta)' : 'var(--white)')};
  color: ${(p) => (p.$variant === 'primary' ? 'var(--white)' : 'var(--primary-500)')};

  &:hover {
    opacity: 0.9;
  }
`

// Stat Cards
export const StatCardsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-4);

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

export const StatCard = styled.div<{$urgency?: UrgencyLevel}>`
  background: var(--white);
  border-radius: var(--radius-lg);
  padding: 18px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
`

export const StatCardBadgeRow = styled.div`
  display: flex;
  gap: var(--space-3);
`

export const StatCardBadge = styled.span<{$textColor: string; $bgColor: string}>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) 6px;
  border-radius: var(--radius-sm);
  font-size: 12px;
  font-weight: 500;
  color: ${(p) => p.$textColor};
  background: ${(p) => p.$bgColor};
`

export const StatCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
`

export const StatCardTitle = styled.h3`
  font-size: 18px;
  font-weight: 500;
  color: #08112a;
  margin: 0;
`

export const StatCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
`

export const StatCardMetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: 14px;
  font-weight: 400;
  color: var(--neutral-600);
`

// Filter tabs
export const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);

  @media (max-width: 968px) {
    flex-wrap: wrap;
  }
`

export const FilterLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: var(--neutral-600);
  white-space: nowrap;
`

export const FilterTabsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-1);
  background: #f1f3f4;
  border-radius: var(--radius-sm);
  padding: 5px;
  /* Match the DS search input height so the pills + search field align. */
  height: var(--input-height-md);
`

export const FilterTab = styled.button<{$active?: boolean}>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 14px;
  height: 32px;
  border-radius: var(--radius-sm);
  border: 1px solid ${(p) => (p.$active ? '#e5e7eb' : 'transparent')};
  cursor: pointer;
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? 500 : 400)};
  color: var(--neutral-700);
  background: var(--white);
  box-shadow: ${(p) => (p.$active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none')};
  white-space: nowrap;
  transition: all 0.15s;

  &:hover {
    border-color: #e5e7eb;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  }
`

export const FilterCount = styled.span<{$color?: string}>`
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => p.$color || 'var(--neutral-700)'};
`

export const SearchInput = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-1);
  background: var(--white);
  border: 1px solid #e5e7eb;
  border-radius: var(--radius-sm);
  padding: var(--space-2) var(--space-3);
  flex: 1;
  min-width: 200px;

  input {
    border: none;
    outline: none;
    font-size: 14px;
    font-weight: 400;
    color: #08112a;
    width: 100%;
    background: transparent;

    &::placeholder {
      color: #757575;
    }
  }

  svg {
    color: #757575;
    flex-shrink: 0;
  }
`

// Open Profile / Open Task button
export const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px var(--space-3);
  border-radius: var(--radius-sm);
  border: none;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: var(--white);
  background: var(--primary-500);
  white-space: nowrap;
  width: fit-content;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.85;
  }
`

// Notification popover styles
export const PopoverContainer = styled.div`
  width: 515px;
  max-height: 560px;
  overflow-y: auto;
  background: var(--white);
  border-radius: var(--radius-lg);
  padding: var(--space-8);
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
`

export const PopoverHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const PopoverTitle = styled.h2`
  font-size: 18px;
  font-weight: 500;
  color: var(--black);
  margin: 0;
`

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--space-1);
  color: var(--black);
  border-radius: var(--radius-sm);

  &:hover {
    background: #f3f4f6;
  }
`

export const NotificationCardContainer = styled.div`
  display: flex;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--white);
  border-radius: var(--radius-sm);
  border: 1px solid #f3f4f6;
`

export const NotificationDot = styled.div<{$color: string}>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  flex-shrink: 0;
  margin-top: 5px;
`

export const NotificationAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: var(--radius-lg);
  background: #e5e7eb;
  flex-shrink: 0;
`

export const NotificationContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  flex: 1;
  min-width: 0;
`

export const NotificationText = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: var(--black);
  margin: 0;
  line-height: 1.5;
`

export const NotificationMeta = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: 12px;
  font-weight: 500;
  color: #aeaeae;
  min-width: 0;
`

export const MetaText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 140px;
`

export const MetaDivider = styled.div`
  width: 1px;
  height: 12px;
  background: #e5e7eb;
`

export const ViewAllButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 6px var(--space-3);
  border-radius: var(--radius-sm);
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: var(--brand-cta);
  background: transparent;
  transition: background 0.15s;

  &:hover {
    background: #f3f4f6;
  }
`

// Bell icon styles
export const BellWrapper = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  color: var(
    --brand-cta
  ); /* clickable nav icon — interaction cyan (identity --brand is 2.6:1, fails) */
  margin-right: var(--space-3);

  &:hover {
    background: var(--neutral-100);
  }
`

export const BellBadge = styled.span`
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 16px;
  height: 16px;
  padding: 0 var(--space-1);
  border-radius: var(--radius-sm);
  background: #e74c3c;
  color: var(--white);
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`

// Table candidate cell with avatar
export const CandidateCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

export const CandidateAvatar = styled.div<{$bgColor?: string}>`
  width: 34px;
  height: 34px;
  border-radius: var(--radius-lg);
  background: ${(p) => p.$bgColor || '#f1f3f4'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: var(--neutral-600);
  flex-shrink: 0;
`

export const CandidateInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`

export const CandidateName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #08112a;
`

export const CandidateId = styled.span`
  font-size: 12px;
  color: var(--neutral-600);
`

export const ExpiredDate = styled.span`
  color: #ef4444;
  font-size: 12px;
  font-weight: 400;
`

export const TableWrapper = styled.div`
  background: var(--white);
  border-radius: var(--radius-sm);
  border: 1px solid #e5e7eb;
  box-shadow: var(--shadow-regular, 0 1px 8px rgba(0, 0, 0, 0.03));
  overflow-x: auto;
`

export const EmptyState = styled.div`
  text-align: center;
  padding: var(--space-12);
  color: var(--neutral-600);
`

export const LastNotifiedCell = styled.span`
  color: #9aa0a6;
  font-size: 12px;
`
